import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, Trash2, Plus, Loader2, FileText } from 'lucide-react';
import { documentService } from '../services/documentService';
import CreateFolderModal from './FileManager/CreateFolderModal';
import FolderDestinationPicker, { ROOT_KEY } from './FileManager/FolderDestinationPicker';
import { useLanguage } from '../i18n';
import { userErrorMessage } from '../utils/userError';
import Modal from './ui/Modal';

/**
 * "Manage Document" — the dialog behind a chat document card's Manage control
 * (and the post-creation auto-pop).
 *
 * Name saving failure front end (#330) redesigned it. It used to be two
 * MUTUALLY EXCLUSIVE, individually auto-saving actions:
 *   - a click-to-edit filename committed by a small Check icon, and
 *   - a "Move to folder" drawer committed by its own "Move Here" button,
 * each of which fired `onDocumentUpdated` and therefore CLOSED the dialog — so
 * renaming and re-filing in one interaction was impossible, and #244's
 * "commit-on-close" existed only to rescue an edit the auto-save had missed.
 *
 * Now: ONE form holding BOTH the filename field and the destination picker, and
 * ONE explicit Save that applies the rename (if changed) and then the move (if
 * changed) in sequence. Nothing is written until Save is pressed; Escape / X /
 * Cancel discard the draft — which SUPERSEDES #244's commit-on-close, because
 * with an explicit Save there is no longer a pending edit to silently lose.
 * A rejected rename (409 duplicate name) surfaces inline and keeps the dialog
 * open on the offending value.
 *
 * The destination list is the shared `FolderDestinationPicker` (extracted from
 * the File Manager's Move dialog, #255) so both surfaces offer exactly the same
 * destinations, with the same protected-folder rules.
 */
const CreatedDocumentModal = ({
    documentId,
    documentName,
    // The folder the document is in right now — supplied by the chat card from
    // the (BE-hydrated) card metadata. `undefined` = unknown (e.g. the modal
    // auto-popped mid-stream), in which case no row is marked "Current" and any
    // pick is treated as a move.
    currentFolderId,
    onClose,
    onDocumentUpdated,
}) => {
    const { t } = useLanguage();

    const [nameInput, setNameInput] = useState(documentName || '');
    const [savedName, setSavedName] = useState(documentName || '');
    const nameInputRef = useRef(null);

    const knowsCurrentFolder = currentFolderId !== undefined;
    const initialKey = knowsCurrentFolder ? (currentFolderId || ROOT_KEY) : null;
    const [selectedKey, setSelectedKey] = useState(initialKey);
    const [savedKey, setSavedKey] = useState(initialKey);

    const [showCreateFolder, setShowCreateFolder] = useState(false);
    const [folderRefresh, setFolderRefresh] = useState(0);
    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);

    const trimmedName = nameInput.trim();
    const nameChanged = !!trimmedName && trimmedName !== savedName;
    const folderChanged = selectedKey !== null && selectedKey !== savedKey;
    const hasChanges = nameChanged || folderChanged;

    // Escape / backdrop cancel — the draft is discarded, nothing is written.
    // (An explicit Save is the only write path, so there is no pending edit to
    // rescue.) While the nested Create Folder dialog is open it is the topmost
    // <Modal>, so Escape closes only that one.
    const cancel = () => { if (!isProcessing) onClose(); };

    // Select the stem (not the extension) so the common case — renaming the file
    // without retyping ".pdf" — is one keystroke away.
    useEffect(() => {
        const el = nameInputRef.current;
        if (!el) return;
        el.focus();
        const dotIdx = (documentName || '').lastIndexOf('.');
        el.setSelectionRange(0, dotIdx > 0 ? dotIdx : (documentName || '').length);
    }, [documentName]);

    const targetFolderId = useMemo(
        () => (selectedKey === ROOT_KEY ? null : selectedKey),
        [selectedKey],
    );

    // The one write path. Rename first, then move — so a 409 on the name stops
    // before anything is relocated, and the error names the field the user must
    // fix. A move failure after a successful rename is reported while the dialog
    // stays open with the rename already banked (savedName advances), so pressing
    // Save again retries only the part that failed.
    const handleSave = useCallback(async () => {
        if (isProcessing || !hasChanges) return;
        setIsProcessing(true);
        setError(null);

        let finalName = savedName;
        let renamed = false;
        try {
            if (nameChanged) {
                const resp = await documentService.renameDocument(documentId, trimmedName);
                // Use the name the BE actually stored — it preserves/appends the
                // extension, so the confirmation matches the stored filename.
                finalName = resp?.original_filename || trimmedName;
                setSavedName(finalName);
                setNameInput(finalName);
                renamed = true;
                window.dispatchEvent(new CustomEvent('omnis:document-renamed', {
                    detail: { id: documentId, name: finalName },
                }));
            }
        } catch (err) {
            setError(userErrorMessage(err, t));
            setIsProcessing(false);
            return;
        }

        let moved = false;
        try {
            if (folderChanged) {
                await documentService.moveItems([], [documentId], targetFolderId);
                setSavedKey(selectedKey);
                moved = true;
                window.dispatchEvent(new CustomEvent('omnis:document-moved', {
                    detail: { id: documentId, folder_id: targetFolderId },
                }));
            }
        } catch (err) {
            setError(userErrorMessage(err, t));
            setIsProcessing(false);
            return;
        }

        setIsProcessing(false);
        onDocumentUpdated?.({
            action: 'saved',
            documentId,
            name: finalName,
            folderId: moved ? targetFolderId : undefined,
            renamed,
            moved,
        });
    }, [
        isProcessing, hasChanges, nameChanged, folderChanged, trimmedName, savedName,
        documentId, targetFolderId, selectedKey, onDocumentUpdated, t,
    ]);

    const handleDiscard = async () => {
        setIsProcessing(true);
        setError(null);
        try {
            await documentService.deleteDocument(documentId);
            onDocumentUpdated?.({ action: 'discarded', documentId });
            onClose();
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setIsProcessing(false);
        }
    };

    // A folder created here must be immediately pickable, so the tree is
    // refetched and the new folder pre-selected as the destination.
    const handleCreateFolder = async (folderName) => {
        const created = await documentService.createFolder(folderName, targetFolderId);
        setShowCreateFolder(false);
        setFolderRefresh((n) => n + 1);
        if (created?.id) setSelectedKey(created.id);
    };

    return (
        <>
            <Modal
                onClose={cancel}
                size="lg"
                layout="column"
                surface="bg-white/[0.03] rounded-xl shadow-modal-sm"
                cardClassName="mx-4 max-h-[85dvh]"
                cardTestId="manage-doc-dialog"
            >
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b border-pai-subtle">
                        <div className="flex items-center gap-2 min-w-0">
                            <FileText size={18} className="text-pai-meta shrink-0" />
                            <h3 className="text-sm font-semibold text-white truncate">{t('fmManageDocument', 'Manage Document')}</h3>
                        </div>
                        <button
                            onClick={() => { if (!isProcessing) onClose(); }}
                            className="p-1 rounded-md hover:bg-white/[0.07] text-pai-meta transition-colors shrink-0"
                            aria-label={t('commonClose', 'Close')}
                        >
                            <X size={18} />
                        </button>
                    </div>

                    {/* Body — ONE form: name AND destination, edited together. */}
                    <div className="p-4 space-y-4 overflow-y-auto min-h-0 flex-1">
                        {error && (
                            <div
                                data-testid="manage-doc-error"
                                className="p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg text-xs text-red-600 dark:text-red-400"
                            >
                                {error}
                            </div>
                        )}

                        <div>
                            <label
                                htmlFor="manage-doc-name"
                                className="text-xs font-medium text-pai-meta uppercase tracking-wide mb-1.5 block"
                            >
                                {t('fmFilename', 'Filename')}
                            </label>
                            <input
                                id="manage-doc-name"
                                data-testid="manage-doc-name"
                                ref={nameInputRef}
                                type="text"
                                value={nameInput}
                                onChange={(e) => setNameInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') { e.preventDefault(); handleSave(); }
                                }}
                                className="w-full px-3 py-2 text-sm rounded-lg border border-pai bg-white/[0.06] text-white text-pai-secondary focus:outline-none focus:ring-2 "
                                placeholder={t('fmEnterNewName', 'Enter new name')}
                            />
                        </div>

                        <div className="flex flex-col min-h-0">
                            <div className="flex items-center justify-between mb-1.5 gap-2">
                                <label className="text-xs font-medium text-pai-meta uppercase tracking-wide">
                                    {t('fmLocation', 'Location')}
                                </label>
                                <button
                                    type="button"
                                    onClick={() => setShowCreateFolder(true)}
                                    className="flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-pai text-pai-secondary hover:bg-white/[0.07]  transition-colors shrink-0"
                                >
                                    <Plus size={13} /> {t('fmNewFolderCap', 'New Folder')}
                                </button>
                            </div>
                            <FolderDestinationPicker
                                currentFolderId={currentFolderId || null}
                                knowsCurrentFolder={knowsCurrentFolder}
                                selectedKey={selectedKey}
                                onSelect={setSelectedKey}
                                listClassName="max-h-52"
                                refreshToken={folderRefresh}
                            />
                        </div>

                        <div className="border-t border-pai-subtle" />

                        {/* Discard — destructive, deliberately separate from Save. */}
                        {!showDiscardConfirm ? (
                            <button
                                type="button"
                                data-testid="manage-doc-discard"
                                onClick={() => setShowDiscardConfirm(true)}
                                className="w-full flex items-center gap-3 p-3 rounded-lg border border-pai-subtle hover:border-red-300 dark:hover:border-red-600 bg-white/[0.06] transition-colors group text-left"
                            >
                                <div className="p-2 rounded-lg bg-white/[0.07] text-pai-meta text-pai-secondary group-hover:bg-red-100 dark:group-hover:bg-red-900/30 group-hover:text-red-500 transition-colors">
                                    <Trash2 size={18} />
                                </div>
                                <div>
                                    <div className="text-sm font-medium text-pai-secondary group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">{t('fmDiscard', 'Discard')}</div>
                                    <div className="text-xs text-pai-meta">{t('fmDeletePermanentlyHint', 'Permanently delete this document')}</div>
                                </div>
                            </button>
                        ) : (
                            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-700">
                                <p className="text-xs text-red-600 dark:text-red-400 mb-2">{t('fmDiscardConfirm', 'This will permanently delete the document. Are you sure?')}</p>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowDiscardConfirm(false)}
                                        className="flex-1 px-3 py-1.5 text-xs rounded-md border border-pai text-pai-secondary hover:bg-white/[0.07]  transition-colors"
                                    >
                                        {t('commonCancel', 'Cancel')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleDiscard}
                                        disabled={isProcessing}
                                        className="flex-1 px-3 py-1.5 text-xs rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors font-medium"
                                    >
                                        {isProcessing ? <Loader2 size={14} className="animate-spin" /> : t('fmConfirmDelete', 'Confirm Delete')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer — the single, explicit commit for BOTH edits. */}
                    <div className="p-4 border-t border-pai-subtle flex items-center justify-end gap-3">
                        <button
                            type="button"
                            data-testid="manage-doc-cancel"
                            onClick={() => { if (!isProcessing) onClose(); }}
                            className="px-4 py-2 text-sm rounded-lg text-pai-secondary hover:bg-white/[0.07] transition-colors"
                        >
                            {t('commonCancel', 'Cancel')}
                        </button>
                        <button
                            type="button"
                            data-testid="manage-doc-save"
                            onClick={handleSave}
                            disabled={isProcessing || !hasChanges}
                            className={`px-5 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                                isProcessing || !hasChanges
                                    ? 'bg-white/[0.07] text-pai-faint cursor-not-allowed'
                                    : 'bg-white text-pai-void hover:bg-white/90'
                            }`}
                        >
                            {isProcessing && <Loader2 size={14} className="animate-spin" />}
                            {isProcessing ? t('fmSavingEllipsis', 'Saving…') : t('commonSave', 'Save')}
                        </button>
                    </div>
                </Modal>

            {showCreateFolder && (
                <CreateFolderModal
                    onClose={() => setShowCreateFolder(false)}
                    onCreate={handleCreateFolder}
                />
            )}
        </>
    );
};

export default CreatedDocumentModal;
